import { generateErrorReport, getSalesTaxFile } from '@commenda/api';
import {
  Job,
  JobEntity,
  JobOperation,
  JobStatus,
} from '@commenda/api/new-sales-tax/job/types';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Tooltip,
} from '@commenda/kakashi';
import { Row } from '@tanstack/react-table';
import { useState } from 'react';

import { IndirectTaxJobHelper } from '../helpers/IndirectTaxJobHelper';

interface IndirectTaxJobsLogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobs: Job[];
  corporationId: number;
  jobOperation?: JobOperation;
  title?: string;
}

const IndirectTaxJobsLogDialog = ({
  isOpen,
  onClose,
  jobs,
  corporationId,
  jobOperation = JobOperation.IMPORT,
  title = 'Product reports',
}: IndirectTaxJobsLogDialogProps) => {
  const [generatingReportJobId, setGeneratingReportJobId] = useState<
    string | undefined
  >();

  const getReportFile = async (fileId: string | undefined) => {
    if (!fileId) return;
    const fileResponse = await getSalesTaxFile(fileId, corporationId);
    if (fileResponse.success && fileResponse.data?.data?.signed_url) {
      window.open(fileResponse.data.data.signed_url, '_blank');
    }
  };

  const downloadErrorReport = async (job: Job) => {
    setGeneratingReportJobId(job.id);
    try {
      let fileId = job.error_report_file_id;
      if (!fileId) {
        const response = await generateErrorReport(job.id, corporationId);
        fileId = response.data?.data?.error_report_file_id;
        if (!response.success || !fileId) return;
      }
      await getReportFile(fileId);
    } finally {
      setGeneratingReportJobId(undefined);
    }
  };

  const columnGroups = [
    {
      id: 'id',
      columns: [
        {
          accessorKey: 'id',
          header: 'Task ID',
          title: 'Task ID',
          width: 250,
          cell: ({ row }: { row: Row<Job> }) => (
            <p className="txt-small-regular text-text-primary truncate">
              {row.getValue('id')}
            </p>
          ),
        },
      ],
    },
    {
      id: 'user',
      columns: [
        {
          accessorKey: 'user',
          header: 'User',
          title: 'User',
          cell: ({ row }: { row: Row<Job> }) => {
            const user: Job['user'] = row.getValue('user');
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {user?.name ?? '-'}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'submitted_at',
      columns: [
        {
          accessorKey: 'submitted_at',
          header: 'Submitted at',
          title: 'Submitted at',
          showInSelector: false,
          cell: ({ row }: { row: Row<Job> }) => {
            const timestamp = Number(row.getValue('submitted_at')) / 1000;
            const date = new Date(timestamp);
            const formattedDate = date
              ? IndirectTaxJobHelper.formatDateTime(date)
              : '-';
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {formattedDate}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'file',
      columns: [
        {
          accessorKey: 'file_name',
          showInSelector: false,
          header: 'File name',
          title: 'File name',
          width: 250,
          cell: ({ row }: { row: Row<Job> }) => {
            const value = (row.getValue('file_name') as string) || '-';
            return (
              <p className="txt-small-regular text-text-primary truncate">
                <Tooltip content={value}>
                  <div className="w-[350px] truncate">{value}</div>
                </Tooltip>
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'entity',
      columns: [
        {
          accessorKey: 'entity',
          header: 'Entity',
          title: 'Entity type',
          cell: ({ row }: { row: Row<Job> }) => {
            const entity = row.getValue('entity') as JobEntity;
            return (
              <Badge variant="gray">
                {IndirectTaxJobHelper.ENTITY_TO_TEXT[entity]}
              </Badge>
            );
          },
        },
      ],
    },
    {
      id: 'operation',
      columns: [
        {
          accessorKey: 'operation',
          header: 'Operation',
          title: 'Operation type',
          cell: ({ row }: { row: Row<Job> }) => {
            const operation = row.getValue('operation') as JobOperation;
            return (
              <Badge variant="gray">
                {IndirectTaxJobHelper.OPERATION_TO_TEXT[operation]}
              </Badge>
            );
          },
        },
      ],
    },
    {
      id: 'status',
      columns: [
        {
          accessorKey: 'status',
          header: 'Status',
          title: 'Status',
          cell: ({ row }: { row: Row<Job> }) => {
            const status = row.getValue('status') as JobStatus;
            const config = IndirectTaxJobHelper.STATUS_CONFIG[status];
            return <Badge variant={config.variant}>{config.label}</Badge>;
          },
        },
      ],
    },
    {
      id: 'total_rows',
      columns: [
        {
          accessorKey: 'total_rows',
          header: 'Total rows',
          title: 'Total rows',
          cell: ({ row }: { row: Row<Job> }) => {
            const original = row.original;
            return (
              <div className="flex items-center gap-sm">
                <p className="txt-small-regular text-text-primary">
                  {original.total_rows || '-'}
                </p>
                {(original?.success_count ?? 0) > 0 && (
                  <Badge variant="success">
                    Success: {original.success_count}
                  </Badge>
                )}
                {(original?.failure_count ?? 0) > 0 && (
                  <Badge variant="error">
                    Failed: {original.failure_count}
                  </Badge>
                )}
              </div>
            );
          },
        },
      ],
    },
    {
      id: 'actions',
      sticky: 'right' as const,
      columns: [
        jobOperation === JobOperation.EXPORT && {
          id: 'actions',
          accessorKey: 'actions',
          header: '',
          showInSelector: false,
          cell: ({ row }: { row: Row<Job> }) => {
            const original = row.original;
            const hasReports = original.export_file_id;
            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasReports}
                onClick={() => getReportFile(original.export_file_id)}
              >
                <Icons.Download01 className="size-4" />
                <span>Download report</span>
              </Button>
            );
          },
        },
        jobOperation === JobOperation.IMPORT && {
          id: 'actions',
          accessorKey: 'actions',
          header: '',
          showInSelector: false,
          cell: ({ row }: { row: Row<Job> }) => {
            const original = row.original;
            const hasReportableErrors =
              original.status === JobStatus.COMPLETED &&
              (original.failure_count ?? 0) > 0;
            const isGenerating = generatingReportJobId === original.id;
            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasReportableErrors || isGenerating}
                isLoading={isGenerating}
                onClick={() => downloadErrorReport(original)}
              >
                <Icons.Download01 className="size-4" />
                <span>Download error report</span>
              </Button>
            );
          },
        },
      ].filter(Boolean),
    },
  ];

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-white min-w-[700px] max-w-[80vw] flex flex-col max-h-[70vh] p-none overflow-hidden"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="p-3xl overflow-y-auto flex-1">
          <DataTable
            columnGroups={columnGroups}
            data={jobs}
            tableViewConfig={{
              isEnabled: true,
              defaultView: 'regular',
            }}
            rowClassName={() => 'cursor-default hover:bg-primary'}
            columnSelectorConfig={{
              isEnabled: true,
              placeholder: 'Customise columns',
              searchText: 'Search columns',
              notFoundText: 'No columns found',
              selectAllText: 'Show all columns',
              defaultVisibleColumns: [
                'id',
                'entity',
                'submitted_at',
                'operation',
                'status',
                'file_name',
                'total_rows',
                'actions',
              ],
            }}
            searchConfig={{
              isEnabled: true,
              placeholder: 'Search by file name or ID',
              columnsToEnableSearchFor: ['file_name', 'id'],
            }}
            emptyStateConfig={{
              title: 'No jobs found',
              description:
                'There are no jobs to display at this time.',
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            size="sm"
            type="button"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxJobsLogDialog };
